/**
 * Editor de AUTORÍA de un curso: meta (título/descr/categoría), estructura
 * curso→unidad→MÓDULO (espina F2-A: lesson|resource|… vía CourseModuleRegistry)
 * y PUBLICAR al catálogo. Crear una lección crea SIEMPRE su `course_module`
 * (kind='lesson', ref_id=lesson.id). El menú "añadir módulo" lo dirige el
 * registry × course_settings.enabled_module_kinds. Escribe en PowerSync
 * (local-first); el servidor aplica authz autor/editor y sella la versión.
 */
import { type Component, createMemo, createSignal, For, Show } from "solid-js";
import { createStore } from "solid-js/store";
import { msg } from "@lingui/core/macro";
import { Plus, Sparkles, Trash2 } from "lucide-solid";
import {
  addAssignmentModuleMutation,
  addBlockMutation,
  addCourseModuleMutation,
  addForumModuleMutation,
  addLessonModuleMutation,
  addResourceModuleMutation,
  addUnitMutation,
  deleteAssignmentMutation,
  deleteBlockMutation,
  deleteCourseModuleMutation,
  deleteLessonMutation,
  deleteResourceMutation,
  deleteUnitMutation,
  parseBlockContent,
  publishCourseMutation,
  restoreCourseModuleCascadeMutation,
  unpublishCourseMutation,
  updateBlockMutation,
  updateCourseMutation,
  updateLessonMutation,
  updateUnitMutation,
} from "@space/shared";
import type { CourseModule, Lesson } from "@space/powersync";
import { useUndo } from "@/lib/undo";
import { stableRows } from "@/lib/solid/stable-rows";
import { Link } from "@/lib/u-router";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { showToast } from "@/components/ui/toast";
import { useCourseTree, useCourseWrite, useLessonBlocks } from "../../hooks/use-course-data";
import { useCourseModules, useCourseSettings } from "@/features/academy/hooks/use-course-modules";
import { MODULE_KIND_META, moduleRegistry } from "@/features/academy/modules/registry";
// FLAGS: kinds gateados por feature (org activa + curso), registry-driven.
import { useModuleKindFeatures } from "@/features/flags/use-module-kind-features";
import { ResourceModuleEditor } from "@/features/academy/modules/resource-module";
// MEDIA-F3F: editor de autoría del módulo `video`
import { VideoModuleEditor } from "@/features/media/components/video-module";
import { AssignmentModuleEditor } from "@/features/academy/modules/assignment-module";
// F3-C: editor de autoría del módulo `forum`
import { ForumModuleEditor } from "@/features/academy/modules/forum-module";
import { BLOCK_KINDS, defaultDefFor } from "../../lib/registry";
import { BlockEditor } from "./block-editor";
// B8 · autoría por IA: propone, nunca escribe. La revisión pieza a pieza es
// donde se cruzan los DOS registros de validación antes de aplicar.
import { AiGenerateDialog } from "@/features/ai-authoring/ai-generate-dialog";
import { AiProposalReview } from "@/features/ai-authoring/ai-proposal-review";
import { useFeatureEnabled } from "@/features/flags/use-feature-enabled";
import { AI_AUTHORING_KEY } from "@space/shared";

const field =
  "w-full rounded-lg border-2 border-border bg-field-background px-3 py-2 focus-visible:border-primary focus-visible:outline-none";

/** Entrada de la espina por unidad: módulo real o lección legada sin módulo. */
interface SpineEntry {
  key: string;
  kind: string;
  position: number;
  module: CourseModule | null;
  lesson: Lesson | null;
}

export const CourseEditor: Component<{ courseId: string }> = (props) => {
  const { t } = useI18n();
  const courseId = () => props.courseId;
  const { drizzle } = useCourseWrite();
  const { course, units, lessons } = useCourseTree(courseId);
  const modules = useCourseModules(courseId);
  const settings = useCourseSettings(courseId);
  const [selectedLesson, setSelectedLesson] = createSignal<string | null>(null);
  // B8 · autoría por IA. La propuesta abierta se guarda aquí para que la
  // revisión sobreviva a los ecos del write-path (el editor no se remonta).
  const aiAuthoring = useFeatureEnabled(AI_AUTHORING_KEY, { courseId });
  const [aiOpen, setAiOpen] = createSignal(false);
  const [aiProposalId, setAiProposalId] = createSignal<string | undefined>();

  // Identidad ESTABLE por id (F4-D): cada eco del write-path (que además
  // re-sella course.version/updated_at) re-emite objetos nuevos; sin esto los
  // `<For>` del árbol REMONTAN todo el subárbol (y muere el estado interactivo:
  // diálogos abiertos, foco, subidas en curso).
  // `updated_at` lo re-sella el server; `created_at` cambia solo de FORMATO
  // (ISO local vs timestamptz::text del eco).
  const VOLATILE = ["updated_at", "created_at"];
  const stableUnits = stableRows(() => units.data ?? undefined, { ignore: VOLATILE });
  const stableLessons = stableRows(() => lessons.data ?? undefined, { ignore: VOLATILE });
  const stableModules = stableRows(() => modules.data ?? undefined, {
    ignore: VOLATILE,
    jsonFields: ["config"],
  });

  // Borradores de edición de bloques, IZADOS al root del editor (que NO se
  // remonta) — sobreviven a las re-emisiones de sync (ver historial CC).
  const [blockDrafts, setBlockDrafts] = createStore<Record<string, Record<string, unknown>>>({});

  const db = () => drizzle();
  const isPublished = () => course.data?.visibility === "public";

  const lessonById = createMemo(() => {
    const map = new Map<string, Lesson>();
    for (const l of stableLessons()) {
      map.set(l.id, l);
    }
    return map;
  });

  /**
   * Espina por unidad: los `course_module` mandan (orden por position). Las
   * lecciones SIN módulo (datos previos a la migración aún no backfilleados en
   * local) se anexan como entradas legadas para no ocultar contenido.
   */
  // Cache de entradas de la espina: mientras módulo/lección conserven la MISMA
  // referencia (stableRows), la entrada conserva la suya y `<For>` no remonta.
  let spineEntryCache = new Map<string, SpineEntry>();
  const keepEntry = (entry: SpineEntry, nextCache: Map<string, SpineEntry>): SpineEntry => {
    const prev = spineEntryCache.get(entry.key);
    const kept =
      prev &&
      prev.kind === entry.kind &&
      prev.position === entry.position &&
      prev.module === entry.module &&
      prev.lesson === entry.lesson
        ? prev
        : entry;
    nextCache.set(entry.key, kept);
    return kept;
  };

  const spineByUnit = createMemo(() => {
    const map = new Map<string, SpineEntry[]>();
    const referenced = new Set<string>();
    const nextCache = new Map<string, SpineEntry>();
    for (const m of stableModules()) {
      const unitId = m.unit_id ?? "";
      const arr = map.get(unitId) ?? [];
      const lesson = m.kind === "lesson" && m.ref_id ? (lessonById().get(m.ref_id) ?? null) : null;
      if (m.kind === "lesson" && m.ref_id) {
        referenced.add(m.ref_id);
      }
      // Un módulo de lección cuyo detalle aún no sincronizó no se pinta (llega
      // en el mismo stream; evita filas fantasma).
      if (m.kind === "lesson" && !lesson) {
        continue;
      }
      arr.push(
        keepEntry(
          {
            key: m.id,
            kind: m.kind ?? "lesson",
            position: m.position ?? 0,
            module: m,
            lesson,
          },
          nextCache,
        ),
      );
      map.set(unitId, arr);
    }
    for (const l of stableLessons()) {
      if (referenced.has(l.id)) {
        continue;
      }
      const unitId = l.unit_id ?? "";
      const arr = map.get(unitId) ?? [];
      arr.push(
        keepEntry(
          {
            key: `legacy-${l.id}`,
            kind: "lesson",
            position: l.position ?? 0,
            module: null,
            lesson: l,
          },
          nextCache,
        ),
      );
      map.set(unitId, arr);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => a.position - b.position);
    }
    spineEntryCache = nextCache;
    return map;
  });

  /**
   * Kinds habilitados: registry × course_settings.enabled_module_kinds ×
   * FLAGS (feature del kind efectiva-habilitada para org activa + curso) — un
   * kind deshabilitado por la academia desaparece del menú "añadir módulo".
   */
  const kindFeature = useModuleKindFeatures(() => props.courseId);
  const enabledKindMeta = createMemo(() => {
    const enabled = new Set(moduleRegistry.enabledKinds(settings.data?.enabled_module_kinds));
    return MODULE_KIND_META.filter((m) => enabled.has(m.kind) && kindFeature()(m.kind).enabled);
  });

  const saveMeta = (patch: Record<string, unknown>) => {
    const d = db();
    if (d) {
      void updateCourseMutation(d, props.courseId, patch);
    }
  };

  const togglePublish = async () => {
    const d = db();
    if (!d) {
      return;
    }
    if (isPublished()) {
      await unpublishCourseMutation(d, props.courseId);
      showToast(t(msg`Removed from the catalog`), "success");
    } else {
      await publishCourseMutation(d, props.courseId);
      showToast(t(msg`Published to the public catalog`), "success");
    }
  };

  const addUnit = () => {
    const d = db();
    if (d) {
      void addUnitMutation(d, props.courseId, { position: units.data?.length ?? 0 });
    }
  };

  const addModule = (unitId: string, kind: string) => {
    const d = db();
    if (!d) {
      return;
    }
    const position = spineByUnit().get(unitId)?.length ?? 0;
    if (kind === "lesson") {
      void addLessonModuleMutation(d, { courseId: props.courseId, unitId, position });
    } else if (kind === "resource") {
      void addResourceModuleMutation(d, { courseId: props.courseId, unitId, position });
    } else if (kind === "video") {
      // MEDIA-F3F: módulo `video` — sin entidad de detalle; el vídeo se elige en
      // el editor y se guarda en `config.videoAssetId`.
      void addCourseModuleMutation(d, {
        courseId: props.courseId,
        unitId,
        kind: "video",
        position,
        config: {},
      });
    } else if (kind === "assignment") {
      void addAssignmentModuleMutation(d, { courseId: props.courseId, unitId, position });
    } else if (kind === "forum") {
      // F3-C: módulo `forum` — crea la fila `forum` (id determinista) + módulo.
      void addForumModuleMutation(d, { courseId: props.courseId, unitId, position });
    } else {
      // LF-B: kinds sin entidad de detalle propia (live_session y futuros del
      // registry). Antes el botón del menú existía pero este handler no tenía
      // rama → clic SIN EFECTO (no se podía añadir una clase en vivo).
      void addCourseModuleMutation(d, {
        courseId: props.courseId,
        unitId,
        kind,
        position,
        config: {},
      });
    }
  };

  const { registerUndo } = useUndo();
  const removeEntry = (entry: SpineEntry) => {
    const d = db();
    if (!d) {
      return;
    }
    const moduleId = entry.module?.id;
    // F4-A: todos los borrados son soft; el undo restaura módulo + ref en pareja.
    void (async () => {
      if (entry.module) {
        await deleteCourseModuleMutation(d, entry.module.id);
      }
      if (entry.kind === "lesson" && entry.lesson) {
        await deleteLessonMutation(d, entry.lesson.id);
      }
      if (entry.kind === "resource" && entry.module?.ref_id) {
        await deleteResourceMutation(d, entry.module.ref_id);
      }
      if (entry.kind === "assignment" && entry.module?.ref_id) {
        await deleteAssignmentMutation(d, entry.module.ref_id);
      }
      if (moduleId) {
        registerUndo(t(msg`Module moved to trash`), async () => {
          const dd = db();
          if (dd) {
            await restoreCourseModuleCascadeMutation(dd, moduleId);
          }
        });
      }
    })();
  };

  return (
    <div class="mx-auto max-w-3xl space-y-6 p-4 md:p-6">
      <div class="flex items-center justify-between gap-2">
        <Link to="/authoring" class="text-sm text-muted-foreground hover:text-foreground">
          ← {t(msg`My courses`)}
        </Link>
        {/* FLAGS: entrada de IA solo si la academia encendió la capacidad. La
            autoridad sigue siendo el servidor (403 estructurado); esto evita el
            403, no lo sustituye. */}
        <Show when={aiAuthoring().enabled}>
          <Button
            variant="outline"
            size="sm"
            data-testid="ai-authoring-open"
            onClick={() => {
              setAiOpen(true);
            }}
          >
            <Sparkles class="mr-1 h-4 w-4" />
            {t(msg`AI`)}
          </Button>
        </Show>
      </div>

      <Show when={aiAuthoring().enabled}>
        <AiGenerateDialog
          open={aiOpen()}
          onOpenChange={setAiOpen}
          courseId={props.courseId}
          onCreated={setAiProposalId}
        />
        <Show when={aiProposalId()}>
          {(id) => (
            <AiProposalReview
              proposalId={id()}
              courseId={props.courseId}
              onClose={() => {
                setAiProposalId(undefined);
              }}
            />
          )}
        </Show>
      </Show>

      <Show when={!course.isLoading} fallback={<Skeleton class="h-40 w-full rounded-2xl" />}>
        {/* Meta */}
        <div class="space-y-3 rounded-2xl border-2 border-border p-5">
          <input
            class={`${field} text-xl font-bold`}
            data-testid="edit-course-title"
            value={course.data?.title ?? ""}
            onChange={(e) => saveMeta({ title: e.currentTarget.value })}
            placeholder={t(msg`Course title`)}
          />
          <textarea
            class={field}
            data-testid="edit-course-description"
            rows={2}
            value={course.data?.description ?? ""}
            onChange={(e) => saveMeta({ description: e.currentTarget.value })}
            placeholder={t(msg`Description`)}
          />
          <input
            class={field}
            data-testid="edit-course-category"
            value={course.data?.category ?? ""}
            onChange={(e) => saveMeta({ category: e.currentTarget.value })}
            placeholder={t(msg`Category (e.g. Languages)`)}
          />
          <div class="flex items-center justify-between pt-1">
            <span class="text-sm text-muted-foreground">
              {t(msg`Status`)}:{" "}
              <span
                data-testid="course-visibility"
                data-published={isPublished() ? "true" : "false"}
              >
                {isPublished() ? t(msg`Public`) : t(msg`Draft`)}
              </span>{" "}
              · {t(msg`version {version}`, { version: course.data?.version ?? 1 })}
            </span>
            <Button
              data-testid="course-publish-toggle"
              variant={isPublished() ? "outline" : "default"}
              onClick={togglePublish}
            >
              {isPublished() ? t(msg`Unpublish`) : t(msg`Publish`)}
            </Button>
          </div>
        </div>

        {/* Estructura: unidad → módulos (espina) */}
        <div class="space-y-4" data-testid="editor-units">
          <For each={stableUnits()}>
            {(u) => (
              <div class="rounded-2xl border border-border p-4">
                <div class="mb-2 flex items-center gap-2">
                  <input
                    class={`${field} font-semibold`}
                    data-testid="edit-unit-title"
                    value={u.title ?? ""}
                    onChange={(e) =>
                      db() && updateUnitMutation(db()!, u.id, { title: e.currentTarget.value })
                    }
                  />
                  <button
                    type="button"
                    data-testid="delete-unit"
                    onClick={() => db() && deleteUnitMutation(db()!, u.id)}
                    class="text-muted-foreground hover:text-danger"
                  >
                    <Trash2 class="h-4 w-4" />
                  </button>
                </div>
                <ul class="space-y-1" data-testid="unit-modules">
                  <For each={spineByUnit().get(u.id) ?? []}>
                    {(entry) => (
                      <Show
                        when={entry.kind === "lesson" && entry.lesson}
                        fallback={
                          <li data-testid="editor-module" data-module-kind={entry.kind}>
                            <div class="mt-1 flex items-start gap-2">
                              <span class="mt-2 rounded-full bg-joy-berry/15 px-2 py-0.5 text-[11px] font-bold text-joy-berry">
                                {entry.kind}
                              </span>
                              <div class="flex-1">
                                <Show when={entry.kind === "resource" && entry.module}>
                                  <ResourceModuleEditor
                                    courseId={props.courseId}
                                    moduleId={entry.module!.id}
                                    refId={entry.module!.ref_id}
                                  />
                                </Show>
                                {/* MEDIA-F3F: autoría del módulo `video` */}
                                <Show when={entry.kind === "video" && entry.module}>
                                  <VideoModuleEditor
                                    courseId={props.courseId}
                                    moduleId={entry.module!.id}
                                    refId={entry.module!.ref_id}
                                  />
                                </Show>
                                {/* end MEDIA-F3F */}
                                {/* F3-B: autoría del módulo `assignment` */}
                                <Show when={entry.kind === "assignment" && entry.module}>
                                  <AssignmentModuleEditor
                                    courseId={props.courseId}
                                    moduleId={entry.module!.id}
                                    refId={entry.module!.ref_id}
                                  />
                                </Show>
                                {/* F3-C: autoría del módulo `forum` */}
                                <Show when={entry.kind === "forum" && entry.module}>
                                  <ForumModuleEditor
                                    courseId={props.courseId}
                                    moduleId={entry.module!.id}
                                    refId={entry.module!.ref_id}
                                  />
                                </Show>
                                {/* end F3-C */}
                              </div>
                              <button
                                type="button"
                                data-testid="delete-module"
                                onClick={() => removeEntry(entry)}
                                class="mt-2 text-muted-foreground hover:text-danger"
                              >
                                <Trash2 class="h-4 w-4" />
                              </button>
                            </div>
                          </li>
                        }
                      >
                        {(_) => {
                          const l = entry.lesson!;
                          return (
                            <li data-testid="editor-module" data-module-kind="lesson">
                              <div class="flex items-center gap-2">
                                <button
                                  type="button"
                                  data-testid="select-lesson"
                                  onClick={() =>
                                    setSelectedLesson(selectedLesson() === l.id ? null : l.id)
                                  }
                                  class={`flex-1 rounded-lg border px-3 py-2 text-left text-sm ${selectedLesson() === l.id ? "border-primary bg-primary/5" : "border-transparent hover:bg-muted/50"}`}
                                >
                                  {l.title}
                                </button>
                                <input
                                  type="number"
                                  class="w-16 rounded-lg border-2 border-border bg-field-background px-2 py-1 text-sm"
                                  data-testid="edit-lesson-xp"
                                  value={l.xp_reward ?? 10}
                                  onChange={(e) =>
                                    db() &&
                                    updateLessonMutation(db()!, l.id, {
                                      xp_reward: Number(e.currentTarget.value),
                                    })
                                  }
                                />
                                <button
                                  type="button"
                                  data-testid="delete-lesson"
                                  onClick={() => removeEntry(entry)}
                                  class="text-muted-foreground hover:text-danger"
                                >
                                  <Trash2 class="h-4 w-4" />
                                </button>
                              </div>
                              <Show when={selectedLesson() === l.id}>
                                <div class="mt-2">
                                  <input
                                    class={`${field} mb-2 text-sm`}
                                    data-testid="edit-lesson-title"
                                    value={l.title ?? ""}
                                    onChange={(e) =>
                                      db() &&
                                      updateLessonMutation(db()!, l.id, {
                                        title: e.currentTarget.value,
                                      })
                                    }
                                  />
                                  <LessonBlocks
                                    courseId={props.courseId}
                                    lessonId={l.id}
                                    drafts={blockDrafts}
                                    setDraft={(id, def) => setBlockDrafts(id, def)}
                                  />
                                </div>
                              </Show>
                            </li>
                          );
                        }}
                      </Show>
                    )}
                  </For>
                </ul>
                {/* Menú "añadir módulo": registry × enabled_module_kinds. */}
                <div class="mt-2 flex flex-wrap gap-2" data-testid="add-module-menu">
                  <For each={enabledKindMeta()}>
                    {(meta) => (
                      <button
                        type="button"
                        data-testid={meta.testId}
                        onClick={() => addModule(u.id, meta.kind)}
                        class="flex items-center gap-1 text-sm text-primary hover:underline"
                        title={t(meta.hint)}
                      >
                        <Plus class="h-3 w-3" /> {t(meta.label)}
                      </button>
                    )}
                  </For>
                </div>
              </div>
            )}
          </For>
          <Button variant="outline" data-testid="add-unit" onClick={addUnit} class="gap-2">
            <Plus class="h-4 w-4" /> {t(msg`Add unit`)}
          </Button>
        </div>
      </Show>
    </div>
  );
};

/** Bloques de una lección: crear (elige tipo), editar `def`, guardar, borrar. */
const LessonBlocks: Component<{
  courseId: string;
  lessonId: string;
  /** Borradores izados al root (sobreviven a las re-emisiones de sync). */
  drafts: Record<string, Record<string, unknown>>;
  setDraft: (id: string, def: Record<string, unknown>) => void;
}> = (props) => {
  const { t } = useI18n();
  const { drizzle } = useCourseWrite();
  const blocks = useLessonBlocks(() => props.lessonId);
  // Identidad estable por id: cada eco de sync re-emite objetos NUEVOS y `<For>`
  // (keyeado por referencia) REMONTABA todos los BlockEditor — matando estado
  // interactivo interno (p.ej. el diálogo "insertar vídeo" se cerraba solo, y
  // una subida en curso moriría igual). Los drafts ya estaban izados al root
  // por esta misma razón; esto protege el resto del estado.
  // `updated_at` lo re-sella el server; `created_at` solo cambia de formato;
  // `content` es jsonb re-serializado por el eco (igualdad estructural).
  const stableBlocks = stableRows(() => blocks.data ?? undefined, {
    ignore: ["updated_at", "created_at"],
    jsonFields: ["content"],
  });

  const db = () => drizzle();
  // Lectura PURA (sin efectos): el borrador vivo o, si aún no se editó, la `def`
  // parseada del servidor.
  const draftFor = (id: string, content: unknown): Record<string, unknown> =>
    props.drafts[id] ?? parseBlockContent(content);

  const addBlock = (kind: string) => {
    const d = db();
    if (d) {
      void addBlockMutation(d, {
        courseId: props.courseId,
        lessonId: props.lessonId,
        kind,
        content: defaultDefFor(kind),
        position: blocks.data?.length ?? 0,
      });
    }
  };
  /**
   * Guardar el bloque ESPERA a que la escritura local aterrice antes de cantar
   * victoria. Antes era `void updateBlockMutation(...)` + toast inmediato:
   * fire-and-forget contra el SQLite de PowerSync. Si el usuario navegaba (o el
   * test hacía `page.goto`) en la ventana entre el clic y el commit, la
   * escritura MORÍA con la página — el bloque se quedaba con la `def` por
   * defecto (prompt vacío ⇒ definición inválida ⇒ el motor lo puntúa 0) y aun
   * así se le había dicho "Block saved". Ahora el toast es la confirmación real
   * y un fallo se comunica en vez de tragarse.
   */
  const saveBlock = async (id: string, content: unknown) => {
    const d = db();
    if (!d) {
      return;
    }
    try {
      await updateBlockMutation(d, id, { content: draftFor(id, content) });
      showToast(t(msg`Block saved`), "success");
    } catch {
      showToast(t(msg`The block could not be saved`), "error");
    }
  };

  return (
    <div class="space-y-3 rounded-lg bg-muted/30 p-3" data-testid="lesson-blocks">
      <For each={stableBlocks()}>
        {(b) => (
          <div class="rounded-lg border border-border bg-background p-3" data-testid="editor-block">
            <div class="mb-2 flex items-center justify-between">
              <span class="rounded-full bg-accent px-2 py-0.5 text-xs text-accent-foreground">
                {b.kind}
              </span>
              <div class="flex gap-2">
                <button
                  type="button"
                  data-testid="save-block"
                  onClick={() => void saveBlock(b.id, b.content)}
                  class="text-xs text-primary hover:underline"
                >
                  {t(msg`Save`)}
                </button>
                <button
                  type="button"
                  data-testid="delete-block"
                  onClick={() => db() && deleteBlockMutation(db()!, b.id)}
                  class="text-muted-foreground hover:text-danger"
                >
                  <Trash2 class="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            <BlockEditor
              kind={b.kind ?? "quiz"}
              def={draftFor(b.id, b.content)}
              onChange={(def) => props.setDraft(b.id, def)}
              courseId={props.courseId}
            />
          </div>
        )}
      </For>
      <div class="flex flex-wrap gap-1.5">
        <For each={BLOCK_KINDS}>
          {(k) => (
            <button
              type="button"
              data-testid={`add-block-${k.kind}`}
              onClick={() => addBlock(k.kind)}
              class="rounded-lg border border-border px-2.5 py-1 text-xs hover:border-primary/50"
              title={t(k.hint)}
            >
              + {t(k.label)}
            </button>
          )}
        </For>
      </div>
    </div>
  );
};
